"use client";

import { useEffect, useState, type ReactNode } from "react";
import posthog from "posthog-js";
import CarouselStudio, { type Entitlements } from "@/components/carousel/CarouselStudio";
import type { TripDay } from "@/lib/trips";
import type { CarouselDraft } from "@/lib/carousel/tripDraft";
import { EMPTY_COVER } from "@/lib/carousel/defaults";

interface ChecklistResponse {
  subId: string;
  date: string;
  locationName: string | null;
  regionCode: string | null;
  durationHrs: number | null;
  species: {
    code: string;
    commonName: string;
    scientificName: string | null;
    commonNameRu: string | null;
    commonNameKz: string | null;
  }[];
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function formatDuration(min: number): string {
  if (min <= 0) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatDurationClock(hrs: number): string {
  const totalMin = Math.round(hrs * 60);
  return `${Math.floor(totalMin / 60)}:${String(totalMin % 60).padStart(2, "0")}`;
}

export interface CarouselHomeClientProps {
  loggedIn: boolean;
  entitlements: Entitlements;
  trips: TripDay[];
  /** Deep-link from a trip's own page ("Create carousel" button) — opens that trip's editor on mount. */
  initialTripDate?: string;
  initialYear?: number;
  /** The marketing hero (title/sub/signup-hint) — rendered only before a draft is
   * open, so the full-screen studio doesn't sit under a redundant page header. */
  heroContent: ReactNode;
}

export default function CarouselHomeClient({
  loggedIn, entitlements, trips, initialTripDate, initialYear, heroContent,
}: CarouselHomeClientProps) {
  const [draft, setDraft] = useState<CarouselDraft | null>(null);
  const [draftSource, setDraftSource] = useState<"trip" | "public">("public");
  const [loadingTrip, setLoadingTrip] = useState(false);
  const [tripError, setTripError] = useState<string | null>(null);

  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  async function openTrip(date: string, year?: number) {
    setLoadingTrip(true);
    setTripError(null);
    try {
      const params = new URLSearchParams({ date });
      if (year) params.set("year", String(year));
      const res = await fetch(`/api/carousel/trip?${params}`);
      const data = await res.json();
      if (!res.ok) {
        setTripError(data.error ?? "Couldn't load that trip");
        return;
      }
      setDraft(data as CarouselDraft);
      setDraftSource("trip");
    } catch {
      setTripError("Network error — try again");
    } finally {
      setLoadingTrip(false);
    }
  }

  useEffect(() => {
    if (initialTripDate) void openTrip(initialTripDate, initialYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleImport() {
    if (importing || !url.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      const res = await fetch(`/api/carousel/checklist?url=${encodeURIComponent(url)}`);
      const data = await res.json();
      if (!res.ok) {
        setImportError(data.error ?? "Import failed — try again");
        return;
      }
      const cl = data as ChecklistResponse;

      const d = new Date(cl.date);
      const monthLabel = d.toLocaleDateString("en-US", { month: "long" });
      const monthYear = `${monthLabel} ${d.getFullYear()}`.toUpperCase();

      setDraft({
        cover: {
          kickerText: "FIELD DIARY",
          headlineTitle: cl.locationName ?? "Field trip",
          subtitle: cl.regionCode ? `${cl.regionCode} · ${monthYear}` : monthYear,
          stats: [
            { icon: "📍", value: cl.locationName ?? "—" },
            { icon: "🕐", value: cl.durationHrs != null ? formatDurationClock(cl.durationHrs) : "—" },
            { icon: "🐦", value: `${cl.species.length} species` },
            { icon: "📅", value: monthLabel },
          ],
        },
        species: cl.species.map((s) => ({
          code: s.code,
          nameEn: s.commonName,
          nameScientific: s.scientificName,
          nameRu: s.commonNameRu,
          nameKz: s.commonNameKz,
        })),
        zipBaseName: `liferradar-${cl.subId}`,
      });
      setDraftSource("public");
      posthog.capture("carousel_checklist_imported", { sub_id: cl.subId, species: cl.species.length });
    } catch {
      setImportError("Network error — try again");
    } finally {
      setImporting(false);
    }
  }

  function startBlank() {
    setDraft({ cover: EMPTY_COVER, species: [], zipBaseName: "liferradar-carousel" });
    setDraftSource("public");
  }

  if (draft) {
    return (
      <CarouselStudio
        initialCover={draft.cover}
        initialSpecies={draft.species}
        entitlements={entitlements}
        source={draftSource}
        zipBaseName={draft.zipBaseName}
        onClose={() => setDraft(null)}
      />
    );
  }

  // Deep-linked from a trip ("Create carousel" on /trips) — skip straight to
  // a loading screen instead of flashing the picker/import page underneath
  // while the trip fetch is in flight.
  if (initialTripDate && !tripError) {
    return (
      <div className="carp-loading">
        <div className="carp-loading-spinner" aria-hidden="true" />
        <p>Loading your trip…</p>
      </div>
    );
  }

  return (
    <div className="carp-page">
      {heroContent}
      {loggedIn && trips.length > 0 && (
        <div className="card trp-list carp-trip-picker">
          {trips.map((t) => (
            <button
              key={t.date}
              type="button"
              className="trp-row"
              onClick={() => openTrip(t.date)}
              disabled={loadingTrip}
            >
              <div className="trp-row-date">
                <div className="trp-row-date-d">{formatDate(t.date)}</div>
                <div className="trp-row-date-m">
                  {t.locations.length > 0 ? t.locations.join(", ") : "Location not recorded"}
                </div>
              </div>
              <div className="trp-row-stats">
                <span className="trp-pill">{t.checklists} {t.checklists === 1 ? "checklist" : "checklists"}</span>
                {t.durationMin > 0 && <span className="trp-pill">{formatDuration(t.durationMin)}</span>}
                {t.lifers > 0 && (
                  <span className="trp-pill trp-pill-lifer">+{t.lifers} {t.lifers === 1 ? "lifer" : "lifers"}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      {loadingTrip && <p className="carp-fallback-label">Loading trip…</p>}
      {tripError && <p className="carp-import-error">{tripError}</p>}

      {loggedIn && (
        <p className="carp-fallback-label">
          {trips.length > 0 ? "Don't see the trip you want, or want a one-off?" : "No trips logged yet —"} start from a CSV or eBird checklist link instead:
        </p>
      )}

      <div className="carp-import">
        <label className="car-label" htmlFor="ebird-url">
          Have an eBird checklist? Paste the link to prefill your species
        </label>
        <div className="carp-import-row">
          <input
            id="ebird-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleImport(); }}
            placeholder="ebird.org/checklist/S123456789"
            className="car-headline-input"
          />
          <button type="button" className="carp-import-btn" onClick={handleImport} disabled={importing || !url.trim()}>
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
        {importError && <p className="carp-import-error">{importError}</p>}
        <button type="button" className="carp-blank-link" onClick={startBlank}>
          …or start with a blank carousel →
        </button>
      </div>
    </div>
  );
}
